import { useCallback, useEffect, useState } from 'react';
import { socialApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmployeeSelect } from '@/components/selects';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import type { SocialComment } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { SocialCrmActionBar } from './SocialCrmActionBar';

interface SocialCommentsWorkspaceProps {
  platformFilter?: string;
}

export function SocialCommentsWorkspace({ platformFilter }: SocialCommentsWorkspaceProps) {
  const pagination = usePagination(15);
  const [comments, setComments] = useState<SocialComment[]>([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [replyDraft, setReplyDraft] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await socialApi.listComments({
        page: pagination.page,
        size: pagination.size,
      });
      let rows = page.content ?? [];
      if (platformFilter) {
        rows = rows.filter((c) => c.platform.toUpperCase() === platformFilter.toUpperCase());
      }
      if (statusFilter !== 'ALL') {
        rows = rows.filter((c) => c.commentStatus === statusFilter);
      }
      setComments(rows);
      pagination.applyClientCount(rows.length);
    } catch (err) {
      showApiError(err, 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, platformFilter, statusFilter, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const reply = async (id: number) => {
    const text = replyDraft[id]?.trim();
    if (!text) return;
    try {
      await socialApi.replyComment(id, text);
      notify.success('Reply published');
      void load();
    } catch (err) {
      showApiError(err, 'Reply failed');
    }
  };

  const resolve = async (id: number) => {
    try {
      await socialApi.resolveComment(id);
      notify.success('Comment resolved');
      void load();
    } catch (err) {
      showApiError(err, 'Resolve failed');
    }
  };

  const assign = async (id: number, employeeId: string) => {
    if (!employeeId) return;
    try {
      await socialApi.assignComment(id, Number(employeeId));
      notify.success('Assigned');
      void load();
    } catch (err) {
      showApiError(err, 'Assign failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'ALL', label: 'All' },
            { value: 'OPEN', label: 'Open' },
            { value: 'REPLIED', label: 'Replied' },
            { value: 'RESOLVED', label: 'Resolved' },
          ]}
        />
      </div>

      {loading && <p className="text-sm text-slate-500">Loading comments…</p>}
      {!loading && comments.length === 0 && (
        <div className="card p-6 text-sm text-slate-500">
          No post comments yet. They appear when webhooks sync or you simulate inbound engagement.
        </div>
      )}
      {comments.map((c) => (
        <article key={c.id} className="card p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span className="font-semibold text-slate-800 dark:text-slate-200">{c.platform}</span>
            <span>· {c.commentStatus}</span>
            <span>· {new Date(c.commentedAt).toLocaleString()}</span>
          </div>
          <p className="text-sm">{c.commentText}</p>
          {c.replyText && (
            <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-400">Replied: {c.replyText}</p>
          )}
          <SocialCrmActionBar sourceType="comment" platform={c.platform} preview={c.commentText} compact />
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <EmployeeSelect
              label="Assign to"
              value=""
              onChange={(v) => v && void assign(c.id, v)}
              placeholder="Assign agent…"
              className="min-w-[200px] flex-1"
            />
            <Button size="sm" variant="secondary" onClick={() => void resolve(c.id)}>
              Resolve
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Input
              className="min-w-0 flex-1"
              value={replyDraft[c.id] ?? ''}
              onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value }))}
              placeholder="Write a public reply…"
            />
            <Button onClick={() => void reply(c.id)}>Reply</Button>
          </div>
        </article>
      ))}
      <TablePagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
        loading={loading}
        hasRows={comments.length > 0}
      />
    </div>
  );
}
